import { useCallback, useEffect, useState } from 'react';
import * as servicesApi from '../api/memberServices';
import { useToast } from './toast';
import { Spinner, ErrorAlert, Field, TextareaField } from './ui';
import ServiceRequestList from './ServiceRequestList';
import { cn, ui } from '../lib/ui';
import { formatMoney } from '../lib/payments';
import { SERVICE_ID_RE, STATUS_LABELS, centsToInput, inputToCents } from '../lib/memberServices';
import { fieldErrorsFrom } from '../lib/formErrors';

const QUEUE = ['approved', 'in_progress', 'pending_approval', 'completed', 'declined', 'cancelled'];

function toForm(service) {
  return {
    name: service?.name || '',
    description: service?.description || '',
    fee: centsToInput(service?.feeCents),
    active: Boolean(service?.active),
    requiresAccreditation: Boolean(service?.requiresAccreditation),
  };
}

function ServiceEditor({ service, isNew, onSaved, onCancel }) {
  const toast = useToast();
  const [serviceId, setServiceId] = useState(service?.serviceId || '');
  const [form, setForm] = useState(() => toForm(service));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key === 'fee' ? 'feeCents' : key]: undefined }));
  };

  async function save(e) {
    e.preventDefault();
    const found = {};
    if (isNew && !SERVICE_ID_RE.test(serviceId)) {
      found.serviceId = 'Use 3 to 50 lowercase letters, numbers or hyphens';
    }
    const feeCents = form.fee.trim() === '' ? undefined : inputToCents(form.fee);
    if (Number.isNaN(feeCents)) found.feeCents = 'Enter an amount such as 250 or 250.00';
    if (Object.keys(found).length) {
      setErrors(found);
      return;
    }
    setSaving(true);
    try {
      const data = await servicesApi.saveMemberService(serviceId, {
        name: form.name,
        description: form.description,
        ...(feeCents !== undefined ? { feeCents } : {}),
        active: form.active,
        requiresAccreditation: form.requiresAccreditation,
      });
      toast.success(`${data.service.name} saved`);
      onSaved(data.service);
    } catch (err) {
      setErrors(fieldErrorsFrom(err));
      toast.error(err.message || 'Failed to save the service');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={save} className="mt-3">
      {isNew && (
        <Field
          label="Service id"
          hint="Lowercase letters, numbers and hyphens; it cannot change later."
          value={serviceId}
          onChange={e => setServiceId(e.target.value.trim())}
          error={errors.serviceId}
        />
      )}
      <Field
        label="Name"
        value={form.name}
        onChange={e => set('name', e.target.value)}
        maxLength={80}
        error={errors.name}
      />
      <TextareaField
        label="Description"
        value={form.description}
        onChange={e => set('description', e.target.value)}
        rows={3}
        maxLength={1000}
        error={errors.description}
      />
      <Field
        label="Flat fee charged to the organization (USD)"
        inputMode="decimal"
        value={form.fee}
        onChange={e => set('fee', e.target.value)}
        placeholder="250.00"
        hint="From 1.00 to 50,000.00. A change applies to requests made after it."
        error={errors.feeCents}
      />
      <label className="mb-2 flex items-center gap-2 text-body-s">
        <input
          type="checkbox"
          className={ui.checkbox}
          checked={form.requiresAccreditation}
          onChange={e => set('requiresAccreditation', e.target.checked)}
        />
        Only verified accredited investors may request it
      </label>
      <label className="mb-4 flex items-center gap-2 text-body-s">
        <input
          type="checkbox"
          className={ui.checkbox}
          checked={form.active}
          onChange={e => set('active', e.target.checked)}
        />
        Offered to members of verified investment organizations
      </label>
      <div className={ui.row}>
        <button className={ui.btnPrimary} type="submit" disabled={saving}>
          {saving ? 'Saving...' : 'Save service'}
        </button>
        <button className={ui.btnGhost} type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * Platform admin -> Member services (docs/Decisions.md D66): the catalog
 * with each service's flat fee, and the queue of requests organizations
 * approved, to start, complete (which charges the org) or decline.
 */
export default function MemberServicesAdminPanel() {
  const [services, setServices] = useState(null);
  const [editing, setEditing] = useState(null);
  const [status, setStatus] = useState('approved');
  const [requests, setRequests] = useState(null);
  const [error, setError] = useState(null);

  const loadCatalog = useCallback(async () => {
    try {
      const data = await servicesApi.getMemberServices();
      setServices(data.services || []);
    } catch (err) {
      setError(err);
    }
  }, []);

  const loadQueue = useCallback(async () => {
    setRequests(null);
    try {
      const data = await servicesApi.listServiceRequests({ scope: 'platform', status });
      setRequests(data.requests || []);
    } catch (err) {
      setError(err);
    }
  }, [status]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);
  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  if (error) return <ErrorAlert error={error} />;
  if (!services) return <Spinner label="Loading member services..." />;

  return (
    <section>
      <h2 className="mt-0 text-title-m">Catalog</h2>
      <p className={cn(ui.hint, 'mb-3')}>
        Members of verified investment organizations can request an offered service; their
        organization pays its flat fee when you mark it complete.
      </p>
      <div className={cn(ui.card, ui.tableWrap, 'mb-4')}>
        <table className={ui.table}>
          <thead>
            <tr>
              <th scope="col">Service</th>
              <th scope="col">Fee</th>
              <th scope="col">Status</th>
              <th scope="col" className="text-right!">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {services.map(service => (
              <tr key={service.serviceId}>
                <td className="align-top">
                  <strong>{service.name}</strong>
                  {service.requiresAccreditation && (
                    <span className={cn(ui.pillInfo, 'ml-2')}>Accredited only</span>
                  )}
                  {editing === service.serviceId && (
                    <ServiceEditor
                      service={service}
                      onSaved={saved => {
                        setServices(prev =>
                          prev.map(s => (s.serviceId === saved.serviceId ? saved : s))
                        );
                        setEditing(null);
                      }}
                      onCancel={() => setEditing(null)}
                    />
                  )}
                </td>
                <td className="align-top">
                  {service.feeCents ? formatMoney(service.feeCents) : 'Not set'}
                </td>
                <td className="align-top">
                  <span className={service.offered ? ui.pillSuccess : ui.pillNeutral}>
                    {service.offered ? 'Offered' : 'Not offered'}
                  </span>
                </td>
                <td className="text-right! align-top">
                  {editing !== service.serviceId && (
                    <button
                      className={cn(ui.btnSecondary, ui.btnSm)}
                      type="button"
                      onClick={() => setEditing(service.serviceId)}
                      aria-label={`Edit ${service.name}`}
                    >
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing === '__new' ? (
        <div className={cn(ui.card, 'mb-6')}>
          <h3 className="mt-0 text-title-s">New service</h3>
          <ServiceEditor
            isNew
            onSaved={saved => {
              setServices(prev => [...prev, saved]);
              setEditing(null);
            }}
            onCancel={() => setEditing(null)}
          />
        </div>
      ) : (
        <button
          className={cn(ui.btnSecondary, 'mb-6')}
          type="button"
          onClick={() => setEditing('__new')}
        >
          Add a service
        </button>
      )}

      <h2 className="text-title-m">Requests</h2>
      <div className={cn(ui.row, 'mb-4')} role="group" aria-label="Show requests that are">
        {QUEUE.map(s => (
          <button
            key={s}
            type="button"
            className={cn(status === s ? ui.btnPrimary : ui.btnSecondary, ui.btnSm)}
            aria-pressed={status === s}
            onClick={() => setStatus(s)}
          >
            {STATUS_LABELS[s]}
          </button>
        ))}
      </div>
      {requests === null ? (
        <Spinner label="Loading requests..." />
      ) : (
        <ServiceRequestList
          requests={requests}
          empty={`No requests are ${STATUS_LABELS[status].toLowerCase()}.`}
          showOrg
          showRequester
          onChanged={updated =>
            setRequests(prev =>
              prev
                .map(r => (r.requestId === updated.requestId ? updated : r))
                .filter(r => r.status === status)
            )
          }
        />
      )}
    </section>
  );
}
